import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Car,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Search,
  FileText,
  Shield,
  CreditCard,
  Zap,
  Users,
  Clock,
} from "lucide-react";
import { Header } from "../components/Header";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqCategories = [
  { id: "all", name: "All Questions", icon: <HelpCircle className="w-5 h-5" /> },
  { id: "general", name: "General", icon: <FileText className="w-5 h-5" /> },
  { id: "pricing", name: "Pricing", icon: <CreditCard className="w-5 h-5" /> },
  { id: "technical", name: "Technical", icon: <Zap className="w-5 h-5" /> },
  { id: "security", name: "Security", icon: <Shield className="w-5 h-5" /> },
];

const faqs: FAQItem[] = [
  {
    category: "general",
    question: "What is Insura AI?",
    answer:
      "Insura AI is an advanced insurance damage analysis platform powered by Claude 3.5 Sonnet. It automatically analyzes vehicle damage photos and documents to generate comprehensive reports with fault analysis, cost estimation, and legal compliance for Turkish insurance claims.",
  },
  {
    category: "general",
    question: "How does the analysis work?",
    answer:
      "Simply upload photos of vehicle damage and relevant documents (accident reports, expert reports, invoices, etc.). Our AI analyzes all images, extracts text from documents, identifies damage areas, calculates fault percentages according to Turkish traffic law, and provides detailed cost estimates. The entire process takes about 2 minutes on average.",
  },
  {
    category: "general",
    question: "What file formats are supported?",
    answer:
      "We support JPEG, PNG, GIF, and WebP image formats. PDF documents are automatically converted to images for analysis. You can upload multiple files at once, and our system will process them together to provide a comprehensive analysis.",
  },
  {
    category: "pricing",
    question: "Is there a free plan?",
    answer:
      "Yes! Our Starter plan is completely free and includes 5 analyses per month, basic damage detection, and PDF report downloads. No credit card required to get started.",
  },
  {
    category: "pricing",
    question: "What's included in the Professional plan?",
    answer:
      "The Professional plan ($499/month) includes 50 analyses per month, advanced damage detection, fault analysis compliant with Turkish traffic law (KTK), detailed cost estimation, PDF & Word reports, and priority email support. Perfect for lawyers and insurance consultants.",
  },
  {
    category: "pricing",
    question: "Can I change plans later?",
    answer:
      "Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges. You can also cancel your subscription at any time without penalties.",
  },
  {
    category: "pricing",
    question: "Do you offer discounts for annual plans?",
    answer:
      "Yes! Contact our sales team for annual billing options. We offer significant discounts for annual subscriptions, especially for Enterprise customers.",
  },
  {
    category: "technical",
    question: "How accurate is the AI analysis?",
    answer:
      "Our platform achieves 98% accuracy in damage detection and fault analysis. The AI is trained on thousands of real insurance cases and continuously improves. However, the analysis should be used as a tool to assist professionals, not as a final legal determination.",
  },
  {
    category: "technical",
    question: "How long does an analysis take?",
    answer:
      "Most analyses are completed within 2 minutes. The time depends on the number of images and documents uploaded. Complex cases with many photos may take up to 5 minutes.",
  },
  {
    category: "technical",
    question: "Can I export the reports?",
    answer:
      "Yes! All plans include PDF report downloads. Professional and Enterprise plans also support Word format. Reports can be exported directly from the dashboard and include all analysis details, images, and legal references.",
  },
  {
    category: "technical",
    question: "Is there an API available?",
    answer:
      "API access is available for Professional and Enterprise plans. The REST API allows you to integrate Insura AI into your existing systems, automate workflows, and process analyses programmatically. Contact us for API documentation and access.",
  },
  {
    category: "security",
    question: "Is my data secure?",
    answer:
      "Absolutely. We use 256-bit SSL encryption for all data transmission and storage. Your uploaded files and analysis results are stored securely and are only accessible to you. We comply with GDPR and Turkish data protection regulations.",
  },
  {
    category: "security",
    question: "Who can access my analysis reports?",
    answer:
      "Only you can access your reports. We never share your data with third parties. All analyses are private and stored securely. You can delete any case or report at any time from your dashboard.",
  },
  {
    category: "security",
    question: "How long is my data stored?",
    answer:
      "Your data is stored for as long as your account is active. You can delete individual cases or your entire account at any time. For Enterprise customers, we offer custom data retention policies.",
  },
  {
    category: "general",
    question: "Do you support Turkish language?",
    answer:
      "Yes! The entire interface, reports, and analysis results are available in Turkish. All legal references comply with Turkish traffic law (2918 Sayılı Karayolları Trafik Kanunu).",
  },
  {
    category: "general",
    question: "What if I need help?",
    answer:
      "We offer email support for all users. Professional plan users get priority support, and Enterprise customers have access to 24/7 phone support. You can also check our documentation and guides in the dashboard.",
  },
];

export function FAQPage() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm mb-6 backdrop-blur-sm">
            <HelpCircle className="w-4 h-4" />
            <span>Frequently Asked Questions</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How can we help you?
          </h1>
          <p className="text-xl text-white/70 mb-8">
            Find answers to common questions about Insura AI
          </p>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-0 focus:ring-2 focus:ring-blue-500 bg-white/10 backdrop-blur-sm text-white placeholder:text-white/50"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {faqCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category.id
                    ? "bg-slate-900 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="space-y-4">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </span>
                    {openIndex === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No questions found matching your search.</p>
              </div>
            )}
          </div>

          {/* Still Have Questions */}
          <div className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Still have questions?
            </h2>
            <p className="text-gray-600 mb-6">
              Can't find the answer you're looking for? Please contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@insura.ai"
                className="px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                <Users className="w-5 h-5" />
                Contact Support
              </a>
              <Link
                to="/pricing"
                className="px-6 py-3 bg-white text-slate-900 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-200 inline-flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-slate-900">Insura AI</span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2024 Insura AI. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Terms of Service
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-700 text-sm">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

